import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import { OrderStatus } from '../../../api/ClientApi';
import { Box, Typography, DialogContent, Grid2 } from '@mui/material';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { getSiteOrdersTableData } from '../OrdersHelper';
import { acceptOrder, clearOrderForOrderAcception, importOrderForAcception } from '../../../redux/actions/orderActions';
import { CompleteOrderType } from '../../../common/Types';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type AcceptOrderFormProps = {
    closeForm(): void;
    open: boolean;
    orderId?: number;
};

export default function AcceptOrderForm(props: AcceptOrderFormProps): ReactElement {
    const { open, closeForm, orderId } = props;

    const order = useSelector((state: RootState) => state.acceptOrderForm.order);
    const saveStatus = useSelector((state: RootState) => state.acceptOrderForm.saveStatus);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    useEffect(() => {
        if (!!orderId && open) {
            dispatch(importOrderForAcception(orderId));
        }
    }, [dispatch, open, orderId]);

    const sites = useSelector((state: RootState) => state.sites.sites);
    const clients = useSelector((state: RootState) => state.clients.clients);

    const [originInfo, setOriginInfo] = useState<string>('/');
    const [destinationInfo, setDestinationInfo] = useState<string>('/');

    useEffect(() => {
        if (!!order) {
            const destinationSite = order.destinationSite;
            const originSite = order.originSite;
            const supplier = order.supplier;

            const destinationData = !destinationSite
                ? '/'
                : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

            const originData = !originSite
                ? '/'
                : getSiteOrdersTableData(originSite, sites ?? undefined, clients ?? undefined);

            setOriginInfo(originData);
            setDestinationInfo(destinationData);
        }
    }, [order]);

    const handleSave = () => {
        dispatch(acceptOrder());
    };

    const handleClose = () => {
        dispatch(clearOrderForOrderAcception());
        closeForm();
    };

    const ogis = useMemo(() => {
        return order?.orderGoodsItems;
    }, [order]);

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>Prihvatanje naloga</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta naloga: <span style={{ fontWeight: 'bold' }}>{'Nalog za otpremanje robe'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Izvor: <span style={{ fontWeight: 'bold' }}>{originInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Odredište: <span style={{ fontWeight: 'bold' }}>{destinationInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />

                    <Grid2 size={{ xs: 12 }}>
                        <Typography
                            variant="body1"
                            component="h6"
                            style={{ fontSize: 20, borderBottom: '1px solid rgba(155,155,155,1)' }}
                        >
                            Roba
                        </Typography>
                    </Grid2>
                    {ogis?.map((ogi) => {
                        return (
                            <Grid2 key={ogi.id} size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        borderBottom: '1px solid rgba(155,155,155,0.3)',
                                    }}
                                >
                                    <Typography>{ogi.article?.title ?? ''}</Typography>
                                    <Typography>{ogi.quantity}</Typography>
                                </Box>
                            </Grid2>
                        );
                    })}
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Prihvati nalog"
                        disabled={saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
